<template>
	<div class="content-page content-page--schedule-dashboard">
		<div class="content-page__heading" ref="contentDashBoardHeading">
			<div class="content-page__breadcrumb-title">
				<Breadcrumbs :items="urlNames" />
				<h2 class="content-page__title">Schedule Dashboard</h2>
			</div>
		</div>

		<div class="schedule-dashboard" v-if="!loading">
			<div class="schedule-dashboard__current">
				<div
					class="schedule-dashboard__current-create"
					v-if="isEmptyCurrentSchedule"
				>
					<div class="schedule-dashboard__title">
						No schedule available
					</div>
					<div class="schedule-dashboard__text">
						Looks like a schedule hasn't been created. Let's try and
						make one!
					</div>
					<Button
						className="btn--primary"
						:labelText="'Create a Scheduled Move'"
						:btnClick="createSchedule"
						:disabled="!hasCreatePermission"
					/>
				</div>

				<div v-else>
					<div class="schedule-dashboard__title">
						Current Schedule
					</div>
					<EnvironmentScheduleCard
						class="schedule-dashboard__card-container"
						:environment="currentSchedule"
						@showStopModal="displayStopModal"
					/>
				</div>
			</div>

			<div class="schedule-dashboard__recent">
				<div class="schedule-dashboard__title">
					Most Recent Schedules
				</div>
				<ScrollContainer
					class="schedule-dashboard__card-container"
					variant="infinity-scroll"
					:hasMoreItems="false"
					:showEndResultByDefault="false"
					v-if="hasResults"
				>
					<EnvironmentScheduleList :items="recentScheduleList" />
				</ScrollContainer>
				<NoResults v-show="!hasResults" data-test="noResults" />
			</div>
		</div>

		<EnvironmentScheduleStopModal
			:schedule="currentSchedule"
			:isStopSchedule="true"
			:titleModal="'Stop scheduled move'"
			v-model="viewStopSchedule"
			@reload="realoadPage"
		/>
		<Spinner :processor="{ loading: loading }" />
	</div>
</template>

<script>
import Button from '@/components/forms/button/button';
import Breadcrumbs from '@/components/shells/breadcrumbs/breadcrumbs';
import EnvironmentScheduleCard from '@/sections/environment/schedule/environment-schedule-card.vue';
import EnvironmentScheduleList from '@/sections/environment/schedule/environment-schedule-list.vue';
import ScrollContainer from '@/components/shells/scroll-container/scroll-container';
import EnvironmentScheduleStopModal from '@/sections/environment/schedule/shared/environment-schedule-stop-modal.vue';
import Spinner from '@/components/visual/spinner/spinner';
import NoResults from '@/site-components/no-results/no-results';

import {
	CURRENT_SCHEDULE,
	RECENT_SCHEDULES,
	SET_RECENT_SCHEDULES
} from '@/sections/environment/environment-mutation-type.js';
import {
	SET_ENVIRONMENTS,
	ENVIRONMENTS
} from '@/sections/options/options-mutation-type.js';
import _ from 'lodash';
//vuex
import { mapActions, mapState } from 'vuex';

export default {
	name: 'EnvironmentScheduleDashBoard',
	components: {
		Button,
		Breadcrumbs,
		EnvironmentScheduleCard,
		EnvironmentScheduleList,
		EnvironmentScheduleStopModal,
		ScrollContainer,
		Spinner,
		NoResults
	},
	data() {
		return {
			viewStopSchedule: false,
			loading: false,
			defaultUrlNames: [
				{
					url: `/environments`,
					name: 'Zone Manager'
				},
				{
					name: 'Dashboard'
				}
			]
		};
	},
	async created() {
		try {
			this.loading = true;
			await this.getApiEnvironments();
			await this.fetchData();
		} finally {
			this.loading = false;
		}
	},
	computed: {
		...mapState('environments', [CURRENT_SCHEDULE, RECENT_SCHEDULES]),

		currentSchedule() {
			return _.get(this, [CURRENT_SCHEDULE], {});
		},
		recentScheduleList() {
			return _.get(this[RECENT_SCHEDULES], 'items', []);
		},
		isEmptyCurrentSchedule() {
			return _.isEmpty(this[CURRENT_SCHEDULE]);
		},
		hasResults() {
			return (
				!this.loading &&
				_.get(this[RECENT_SCHEDULES], 'items', []).length > 0
			);
		},
		urlNames() {
			const routeBreadcrumb = this.$route.meta.breadcrumb;
			const storeBreadcrumb = this.$store.getters.breadcrumbs(
				this.$route.path
			);

			if (!_.isEmpty(routeBreadcrumb)) {
				return routeBreadcrumb;
			}

			if (!_.isEmpty(storeBreadcrumb)) {
				return storeBreadcrumb;
			}

			return this.defaultUrlNames;
		},
		hasCreatePermission() {
			return this.validatePermission('environments.create');
		},
	},
	methods: {
		...mapActions('environments', {
			fetchSchedules: SET_RECENT_SCHEDULES
		}),
		...mapActions('options', {
			getEnvironments: SET_ENVIRONMENTS
		}),
		async fetchData() {
			const payload = { sortBy: 'datetime', count: 6 };
			await this.fetchSchedules(payload);
		},
		createSchedule() {
			this.$router.push({
				path: `/environments/schedule/add`
			});
		},
		displayStopModal(value) {
			this.viewStopSchedule = value;
		},
		async realoadPage(val) {
			if (val) {
				this.viewStopSchedule = false;
				try {
					this.loading = true;
					await this.fetchData();
				} finally {
					this.loading = false;
				}
			}
		},
		async getApiEnvironments() {
			if (_.isEmpty(this[ENVIRONMENTS])) {
				await this.getEnvironments({ active: true });
			}
		}
	},
	beforeRouteLeave(to, from, next) {
		let currentBreadcrumbs = _.cloneDeep(
			_.get(from.meta, 'breadcrumb', this.defaultUrlNames)
		);
		const routePages = [
			'environment-schedule-create',
			'environment-schedule-edit',
			'environment-schedule-reschedule'
		];
		if (routePages.includes(to.name)) {
			const index = currentBreadcrumbs.findIndex(
				obj => obj.name === 'Dashboard' && !obj.url
			);

			if (index !== -1) {
				currentBreadcrumbs[index].url = from.fullPath;
			}

			currentBreadcrumbs.push({ name: 'Schedule' });

			to.meta.breadcrumb = currentBreadcrumbs;
		}

		if (!_.isEmpty(currentBreadcrumbs)) {
			const obj = { path: to.fullPath, breadcrumb: to.meta.breadcrumb };
			this.$store.commit('setBreadCrumbs', obj);
		}

		next();
	}
};
</script>

<style lang="scss" scoped>
/**
	* Schedule Dashboard
	* Can use SCSS variables = TRUE
*/
.schedule-dashboard {
	display: grid;
	grid-template-columns: 1fr;
	gap: $gutter-xl;

	@include mq($from: md-l) {
		grid-template-columns: 1fr 1fr;
		gap: $gutter-xxl;
	}

	&__title {
		@include heading('md');
	}

	&__text {
		@include body-text('sm-supporting');
		color: $black;
		margin-bottom: $gutter-lg;
	}

	&__card-container {
		margin-top: $gutter-lg;
	}
}
</style>
